/**
 * useUsuarios.js - Hook personalizado para gestión de usuarios
 * 
 * RESPONSABILIDAD:
 * - Cargar lista de usuarios con paginación
 * - Buscar usuarios por criterios
 * - Cargar objetos/tags de un usuario específico
 * - Mantener estado de usuarios y total
 * 
 * PROPÓSITO EN EL SISTEMA:
 * - Centralizar lógica de usuarios
 * - Reutilizable en diferentes componentes
 * - Manejo de estado y caché de usuarios
 * 
 * MÉTODOS EXPUESTOS:
 * - loadUsuarios(page, search): Carga usuarios con paginación
 * - loadObjetosUsuario(userId): Carga tags de un usuario
 * 
 * ESTADO MANEJADO:
 * - usuarios: Array de usuarios cargados
 * - totalUsuarios: Cantidad total en BD
 * - loading: Estado de carga
 * - currentPage: Página actual
 * 
 * USO:
 * const { usuarios, loadUsuarios, loading } = useUsuarios();
 */
import { useState } from 'react';
import { getAuthHeaders, fetchWrapper } from '../../../services/api/utils/fetchWrapper';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export function useUsuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [totalUsuarios, setTotalUsuarios] = useState(0);
  const [loading, setLoading] = useState(false);

  const loadUsuarios = async (page = 1, search = '') => {
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      let url = `${API_URL}/api/admin/usuarios?page=${page}&pageSize=20`;
      
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      
      const response = await fetchWrapper(url, { headers });
      
      if (!response.ok) {
        throw new Error('Error al cargar usuarios');
      }
      
      const data = await response.json();
      
      if (data.success) {
        setUsuarios(data.data || []);
        setTotalUsuarios(data.total || 0);
      }
    } catch (error) {
      console.error('Error cargando usuarios:', error);
      setUsuarios([]);
      setTotalUsuarios(0);
    } finally {
      setLoading(false);
    }
  };

  const loadObjetosUsuario = async (userId) => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetchWrapper(
        `${API_URL}/api/admin/usuarios/${userId}/objetos`,
        { headers }
      );
      
      if (!response.ok) {
        const error = await response.json();
        return { error: error.error || 'Error al cargar objetos' };
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error cargando objetos del usuario:', error);
      return { error: 'Error de conexión' };
    }
  };

  return {
    usuarios,
    totalUsuarios,
    loading,
    loadUsuarios,
    loadObjetosUsuario
  };
}